<template>
  <vd-error
    v-if="!queryLoading && notFound"
    status="404"
    message="The class you are looking for does not exist"
    :back-link="backLink"
  />
  <vd-error
    v-else-if="!queryLoading && classId && !inWIPVersion && !preview"
    status="405"
    message="You can not edit the old version of a class"
    :back-link="backLink"
  />
  <vd-error
    v-else-if="!queryLoading && !classId && !inWIPVersion"
    status="405"
    message="You can not link a class in an old version"
    :back-link="backLink"
  />
  <div v-else class="class-form-page">
    <vd-page-form
      class="clazz-form"
      :preview="preview"
      :title="pageTitle"
      :back-link="backLink"
      :edit-link="editLink"
      :form="form"
      :form-changed="formChanged"
      display-cancel-alert
      :hide-actions="!inWIPVersion || !canWriteClasses"
      :save-callback="saveForm"
      :save-loading="saveLoading"
      :loading="queryLoading"
      :allow-delete="!!classId && canWriteClasses"
      @delete="onDelete"
    >
      <VdFormField
        v-model="form.displayName"
        prop="displayName"
        type="text"
        label="Display name"
        :rules="[{ required: true, message: 'the display name is required' }]"
        show-word-limit
        max-length="100"
      />

      <VdFormField
        v-model="form.technicalName"
        prop="technicalName"
        type="text"
        label="Technical name"
        :rules="baseTechnicalNameRules"
        show-word-limit
        :error="formErrors.technicalName"
        max-length="150"
        :disabled="!!classId"
      />

      <VdFormField
        v-model="form.description"
        prop="description"
        type="textarea"
        label="Description"
        class="description"
        show-word-limit
        :auto-size="{ minRows: 3, maxRows: 4 }"
      />

      <!-- Attributes table -->
      <el-table
        :key="form.attributes.map((o) => o.rowId).join('')"
        :data="form.attributes"
        style="width: 100%"
        stripe
        class="attributes-table"
      >
        <el-table-column prop="displayName" label="Display name" fixed :min-width="200">
          <template #default="scope">
            <div class="attribute-name-cell">
              <vd-button
                v-if="!preview"
                type="tertiary"
                class="delete-button"
                :disabled="form.attributes.length === 1"
                data-cy="delete-attribute-button"
                @click="onDeleteAttribute(scope.row.rowId)"
              >
                <vd-custom-icon name="delete-icon" :has-hover-state="form.attributes.length > 1" disabled />
              </vd-button>
              <VdFormField
                :prop="`attributes.${scope.$index}.displayName`"
                type="text"
                placeholder="Display name"
                class="display-name-form-field"
                :disabled="preview"
                :model-value="scope.row.displayName"
                :rules="attributeDisplayNameRules"
                max-length="100"
                inline
                @update:model-value="(value) => onUpdateAttribute(scope.row.rowId, 'displayName', value)"
              />
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="technicalName" label="Technical name" fixed :min-width="200">
          <template #default="scope">
            <VdFormField
              :prop="`attributes.${scope.$index}.technicalName`"
              type="text"
              placeholder="Technical name"
              :disabled="preview"
              :model-value="scope.row.technicalName"
              :rules="attributeTechnicalNameRules"
              max-length="100"
              inline
              @update:model-value="(value) => onUpdateAttribute(scope.row.rowId, 'technicalName', value)"
            />
          </template>
        </el-table-column>

        <el-table-column prop="type" label="Type" :min-width="200" fixed>
          <template #default="scope">
            <VdTooltip
              :disabled="!preview || !scope.row.availableValues || !scope.row.availableValues.length"
              :content="getAvailableValuesTooltipContent(scope.row.availableValues)"
              orientation="top"
            >
              <VdFormField
                :prop="`attributes.${scope.$index}.type`"
                :label="!preview && isSelectOrMultiselectInput(scope.row) ? 'Type' : ''"
                :rules="attributeTypeRules"
                :inline="scope.row.type !== 'select'"
              >
                <VdSelect
                  placeholder="Type"
                  :disabled="preview"
                  :choices="classAttributeTypeChoices"
                  :model-value="scope.row.type"
                  @update:model-value="(value) => onUpdateAttribute(scope.row.rowId, 'type', value)"
                />
              </VdFormField>
            </VdTooltip>
            <VdFormField
              v-if="!preview && isSelectOrMultiselectInput(scope.row)"
              :prop="`attributes.${scope.$index}.availableValues`"
              class="select-values-input"
              label="Values"
              :help-text="
                !!classId
                  ? 'In order to remove existing values, you have to delete this attribute and create a new one'
                  : ''
              "
              :rules="attributeAvailableValuesRules"
            >
              <VdSelect
                multiple
                editable
                :disabled="preview || scope.row.disableAvailableValues"
                placeholder="enter values"
                :model-value="scope.row.availableValues"
                :choices="scope.row.availableValues.map((o: string) => ({ label: o, value: o, disabled: scope.row.savedValues.includes(o) }))"
                @update:model-value="(value) => onUpdateAttribute(scope.row.rowId, 'availableValues', value)"
              />
            </VdFormField>
          </template>
        </el-table-column>

        <el-table-column show-overflow-tooltip align="left" :width="400" prop="">
          <template #header>
            <span class="instance-title">Default values</span>
          </template>
          <template #default="scope">
            <VersatileFormField
              :key="`${scope.$index}-${form.instances[0].id}-${scope.row.rowId}-${scope.row.type}-0`"
              :disabled="preview"
              :multiple="scope.row.isArrayType"
              :model-value="scope.row.instanceValues[0]"
              :type="scope.row.type"
              :rules="scope.row.type === 'select' ? [{ required: true, message: 'this field is required' }] : []"
              :choices="scope.row.availableValues && scope.row.availableValues.map((o: AttributeValue) => ({ label: o, value: o }))"
              :auto-size="{ minRows: 1, maxRows: 6 }"
              :prop="`attributes.${scope.$index}.instanceValues.0`"
              :hide-controls="preview"
              inline
              @update:model-value="(value) => onUpdateInstanceValue(scope.row.rowId, form.instances[0].id, value)"
            />
          </template>
        </el-table-column>
      </el-table>

      <div class="attributes-controls">
        <vd-button v-if="!preview" type="secondary" skin="blue" icon-left="fa:add" @click="onAddAttribute">
          Add attribute
        </vd-button>
      </div>

      <!-- Instances Management Drawer -->
      <div class="drawer-wrapper" @click="toggleDrawer">
        <VdIcon store="fa" :name="isDrawerOpen ? 'chevron-down' : 'chevron-right'" size="large" />
        <span>Instance management</span>
        <VdTooltip
          content="By adding more instances to a class you will be able to configure different values for the attributes.<br />All instances and values will be distributed to the app"
          orientation="top"
        >
          <VdIcon store="fas" name="info-circle" />
        </VdTooltip>
      </div>

      <span class="multi-instances-attributes">
        <VdFormField
          v-if="isDrawerOpen && canUseDynamicInstances"
          v-model="form.dynamicInstances"
          class="dynamic-multi-instances"
          prop="dynamicInstances"
          help-text="Dynamic Multi Instance classes allow overwrites at Remote Config and AB Tests to have dynamic<br />number of instances that don't need to match the number of instances on the Class default"
          label="Use dynamic instances"
          :disabled="!!classId"
        >
          <VdSwitch v-model="form.dynamicInstances" :disabled="!!classId"></VdSwitch>
        </VdFormField>
      </span>

      <!-- Instances table -->
      <el-table
        v-if="isDrawerOpen"
        :key="`${form.instances.map((o) => o.id).join('')}-${form.attributes.map((o) => o.instanceValues[0]).join('')}`"
        :data="form.instances"
        style="width: 100%"
        stripe
        align="left"
        class="attributes-table"
      >
        <el-table-column prop="name" label="Instance" fixed :width="200">
          <template #default="scope">
            <div class="attribute-name-cell">
              {{ getColumnLabel(scope.$index + 1) }}
              <vd-button
                v-if="!preview && scope.$index > 0"
                type="tertiary"
                class="delete-button"
                data-cy="delete-instance-button"
                @click="onDeleteInstance(scope.row.id)"
              >
                <vd-custom-icon name="delete-icon" :has-hover-state="form.instances.length > 1" disabled />
              </vd-button>
            </div>
          </template>
        </el-table-column>

        <el-table-column
          v-for="(attribute, index) in form.attributes"
          :key="attribute.displayName || `Attribute ${index + 1}`"
          :min-width="200"
          show-overflow-tooltip
          prop=""
        >
          <template #header>
            <span class="instance-title">{{ attribute.displayName || `Attribute ${index + 1}` }}</span>
          </template>
          <template #default="instance">
            <VersatileFormField
              :key="`${instance.$index}-${instance.row.id}-${attribute.rowId}-${attribute.type}-${index}`"
              :disabled="preview || instance.$index === 0"
              :multiple="attribute.isArrayType"
              :model-value="attribute.instanceValues[instance.$index]"
              :type="attribute.type"
              :rules="attribute.type === 'select' ? [{ required: true, message: 'this field is required' }] : []"
              :choices="attribute.availableValues && attribute.availableValues.map((o: AttributeValue) => ({ label: o, value: o }))"
              :auto-size="{ minRows: 1, maxRows: 6 }"
              :prop="`attributes.${index}.instanceValues.${instance.$index}`"
              :hide-controls="preview"
              inline
              @update:model-value="(value) => onUpdateInstanceValue(attribute.rowId, instance.row.id, value)"
            />
          </template>
        </el-table-column>
      </el-table>

      <div class="attributes-controls">
        <vd-button
          v-if="isDrawerOpen && !preview"
          type="secondary"
          skin="blue"
          icon-left="fa:add"
          class="add-instance-button"
          @click="onAddInstance"
        >
          Add instance
        </vd-button>
      </div>
    </vd-page-form>

    <!-- warning delete dialog -->
    <vd-dialog
      :visible="displayDeleteWarning"
      hide-close-button
      title="This class can't be deleted"
      type="error"
      @close="closeDeleteWarning"
    >
      <span>You can't delete this class as it is used</span>
      <strong v-if="deleteWarningDetails.configurationRules.length">
        in {{ deleteWarningDetails.configurationRules.length }}
        {{ pluralize("configuration rule", deleteWarningDetails.configurationRules.length) }}
      </strong>
      <span v-if="deleteWarningDetails.abtests.length && deleteWarningDetails.configurationRules.length">
        and&nbsp;
      </span>
      <span v-if="deleteWarningDetails.abtests.length">
        <strong>in the following A/B Test cohorts:</strong>
        <ul class="delete-warning-list">
          <li v-for="abtest in deleteWarningDetails.abtests" :key="abtest.id">
            <em>{{ abtest.cohortName }}</em>
            of the
            <strong>{{ abtest.name }}</strong>
            A/B Test
          </li>
        </ul>
      </span>
      <template #footer>
        <vd-button type="secondary" skin="blue" @click="closeDeleteWarning">I understand</vd-button>
      </template>
    </vd-dialog>
  </div>
</template>

<script setup lang="ts">
import { FormRule, VdFormField, VdSelect, VdSwitch, VdTooltip } from "@voodoo.io/voodoo-ui";
import { computed, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";

import {
  DYNAMIC_INSTANCES_ALLOWED_APP_IDS,
  classAttributeTypeChoices,
  inputTypeToAttributeTypeMapping,
  typeToDefaultValueMapping,
} from "@/constants";
import { pluralize } from "@/filters";
import {
  AttributeValue,
  ClassFormInput,
  ClassFormInputAttribute,
  ClassFormPageClassFragment,
  DeleteClassMutationResponse,
  queryNames,
  useClassFormPageDeleteMutation,
  useClassFormPageQuery,
  useClassFormPageSaveMutation,
  useMutationResponse,
} from "@/graphql";
import useFormTracking from "@/hooks/form-tracking";
import usePermissions from "@/hooks/use-permissions";
import useToast from "@/hooks/useToastNotification";
import links from "@/router/links";
import { generateRandomId } from "@/utils";
import { convertAttributeValue } from "./utils";

const buildInstanceName = (number: number) => `Instance ${number}`;

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },
  versionId: {
    type: String,
    required: true,
  },

  /**
   * If the classId is present, update the class, create it otherwise.
   */
  classId: {
    type: String,
    default: null,
  },

  /**
   * If true, enter in the preview mode
   */
  preview: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();
const { toastError, toastSuccess } = useToast();
const { canWriteClasses } = usePermissions();

/**
 * Define the form
 */

interface FormAttribute extends ClassFormInputAttribute {
  rowId: string;
  disableAvailableValues: boolean;
  instanceValues: AttributeValue[];
  isArrayType?: boolean;
  savedValues: string[];
}

interface FormInstance {
  id: string;
  enabled: boolean;
  name: string;
}

interface Form extends Omit<ClassFormPageClassFragment, "attributes" | "instances" | "type"> {
  attributes: FormAttribute[];
  instances: FormInstance[];
}

const form = reactive<Form>({
  displayName: "",
  technicalName: "",
  description: "",
  isMultiInstance: false,
  dynamicInstances: false,
  attributes: [
    {
      displayName: "",
      technicalName: "",
      type: "string",
      rowId: generateRandomId(),
      availableValues: [] as string[],
      disableAvailableValues: false,
      instanceValues: [""] /* first instance attribute value */,
      savedValues: [] as string[],
    },
  ],
  instances: [
    {
      /* fake id, this is useful to manipulate the instance but it will be
       * cleared before passing to the backend (which will generate the final ID).
       */
      id: `__${generateRandomId()}`,
      name: buildInstanceName(1),
      enabled: true,
    },
  ],
});

const { formChanged, resetTracking: resetFormTracking } = useFormTracking(form);

const canUseDynamicInstances = computed(() => DYNAMIC_INSTANCES_ALLOWED_APP_IDS.includes(props.gameId));

/**
 * Form rules
 */
const attributeDisplayNameRules = [{ required: true, message: "the name is required" }];

const isValidTechnicalClassNameIdentifier = (value: string) => {
  const nestedAndParent = value.split("+");
  return nestedAndParent.every((currentValue: string) =>
    /^[a-zA-Z_][a-zA-Z0-9_]*(\.[a-zA-Z_][a-zA-Z0-9_]*)*$/.test(currentValue)
  );
};

const baseTechnicalNameRules: FormRule[] = [
  { required: true, message: "the technical name is required" },
  {
    type: "string",
    validator(_: unknown, value: AttributeValue, callback: (error?: Error) => void) {
      let error: Error | undefined;
      if (!isValidTechnicalClassNameIdentifier(String(value))) {
        error = new Error(`Invalid technical class name.
                Please use a valid identifier format, including namespaces separated by dots.`);
      }
      if (String(value).length > 150) {
        error = new Error("the technical name is too long");
      }
      callback(error);
    },
  },
];

const attributeTechnicalNameRules: FormRule[] = [
  ...baseTechnicalNameRules,
  {
    type: "string",
    validator(_: unknown, value: AttributeValue, callback: (error?: Error) => void) {
      let error: Error | undefined;
      if (form.attributes.filter((attr) => attr.technicalName === value).length > 1) {
        error = new Error("the technical name is already used");
      }
      callback(error);
    },
  },
];

const attributeTypeRules = [{ required: true, message: "the type is required" }];

const attributeAvailableValuesRules: FormRule[] = [
  {
    type: "array",
    validator(_: unknown, availableValues: string[], callback: (error?: Error) => void) {
      let error: Error | undefined;
      if (availableValues.length === 0) {
        error = new Error("please, enter some values");
      }
      callback(error);
    },
  },
];

const formErrors = reactive({
  technicalName: "",
});

const onAddInstance = () => {
  form.attributes = form.attributes.map((attribute) => ({
    ...attribute,
    instanceValues: [...attribute.instanceValues, typeToDefaultValueMapping[attribute.type]],
  }));

  form.instances = [
    ...form.instances,
    {
      id: `__${generateRandomId()}`,
      name: buildInstanceName(form.instances.length + 1),
      enabled: true,
    },
  ];
};

watch(form, () => {
  formErrors.technicalName = "";
});

/**
 * Try to fetch the class from its id.
 * If we have a class, update the form title and fill the form
 */
const { result, loading: queryLoading } = useClassFormPageQuery(
  {
    gameId: props.gameId,
    versionId: props.versionId,
    classId: props.classId,
  },
  { fetchPolicy: "network-only" }
);

const clazz = computed(() => result.value?.game?.version?.class);

const notFound = computed(() => !queryLoading.value && !result.value?.game?.version?.class && props.classId);

const inWIPVersion = computed(() => result.value?.game?.version?.isWip);

/**
 * Handle the page title
 */
const pageTitle = computed(() => {
  if (props.classId) {
    return (clazz.value && clazz.value.displayName) || "";
  }
  return "New class";
});

/**
 * Fill the form with the value fetched
 */
watch(clazz, async (value) => {
  if (value) {
    form.displayName = value.displayName;
    form.technicalName = value.technicalName;
    form.description = value.description;
    form.isMultiInstance = value.isMultiInstance;
    form.dynamicInstances = value.dynamicInstances;
    form.attributes = value.attributes.map((attr) => ({
      ...attr,
      type: attr.formFieldType,
      availableValues: attr.availableValues || [],
      rowId: generateRandomId(),
      disableAvailableValues: !["select", "multiselect"].includes(attr.formFieldType),
      savedValues: [...(attr.availableValues || [])],
      instanceValues: value.instances.map((instance) => {
        const valuesByTechnicalName = Object.fromEntries(
          instance.values.map(({ technicalName, defaultValue }) => [technicalName, defaultValue])
        );
        return valuesByTechnicalName[attr.technicalName] ?? typeToDefaultValueMapping[attr.type];
      }),
    }));
    form.instances = value.instances.map((instance, index) => ({
      id: instance.id,
      name: buildInstanceName(index + 1),
      enabled: true,
    }));

    resetFormTracking();
  }
});

const isDrawerOpen = ref(false);

const toggleDrawer = () => {
  isDrawerOpen.value = !isDrawerOpen.value;
};

watch(
  () => form.isMultiInstance,
  (isMultiInstance) => {
    /**
     * If the user switch from multi-instance to mono-instance
     * we have keep only one instance (ie the first)
     */
    if (!isMultiInstance) {
      if (form.instances.length < 1) {
        onAddInstance();
      }
      form.instances = form.instances.slice(0, 1);

      form.attributes = form.attributes.map((attribute) => ({
        ...attribute,
        instanceValues: attribute.instanceValues.slice(0, 1),
      }));

      resetFormTracking();
    }
  }
);

const onAddAttribute = () => {
  form.attributes = [
    ...form.attributes,
    {
      displayName: "",
      technicalName: "",
      type: "string",
      availableValues: [],
      rowId: generateRandomId(),
      disableAvailableValues: false,
      instanceValues: new Array(form.instances.length).fill(""),
      savedValues: [] as string[],
    },
  ];
};

const getAvailableValuesTooltipContent = (availableValues?: string[]) =>
  `${(availableValues ?? []).join(", ").substring(0, 70)}...`;

/**
 * Knowing if a field is a select input
 */
const isSelectOrMultiselectInput = (attribute: FormAttribute) => {
  if (["select", "multiselect"].includes(attribute.type)) {
    return true;
  }
  if (["string", "string[]"].includes(attribute.type)) {
    return (attribute.availableValues || []).length > 0;
  }
  return false;
};

/**
 * Attribute helpers
 */
const onDeleteAttribute = (rowId: string) => {
  form.attributes = form.attributes.filter((attribute) => attribute.rowId !== rowId);
};

/**
 * Update all the properties of one attribute
 */
const onUpdateAttribute = (rowId: string, propName: string, value: AttributeValue) => {
  form.attributes = form.attributes.map((attribute) => {
    if (attribute.rowId === rowId) {
      /**
       * If the type is modified, attempt to preserve existing values when possible
       */
      if (propName === "type") {
        const typeValue = (value || "").toString();
        const oldType = attribute.type;
        const oldInstanceValues = [...attribute.instanceValues];

        // Try to preserve values when changing types
        const newInstanceValues = oldInstanceValues.map((oldValue) =>
          convertAttributeValue(oldValue, oldType, typeValue)
        );

        const updateAttributeResult = {
          ...attribute,
          [propName]: typeValue,
          instanceValues: newInstanceValues,
        };

        if (!["select", "multiselect"].includes(typeValue)) {
          return { ...updateAttributeResult, availableValues: [] };
        }

        // If we switch to a select type the user needs to define an instance value
        if (typeValue === "select" && attribute.instanceValues.length > 1) {
          isDrawerOpen.value = true;
        }

        return updateAttributeResult;
      }
      return { ...attribute, [propName]: value };
    }
    return attribute;
  });
};

const onUpdateInstanceValue = (attributeId: string, instanceId: string, value: AttributeValue) => {
  const instanceIndex = form.instances.findIndex((instance) => instance.id === instanceId);

  /** update the value on all attributes' instanceValues */
  form.attributes = form.attributes.map((attribute) => {
    if (attribute.rowId === attributeId) {
      const instanceValues = [
        ...attribute.instanceValues.slice(0, instanceIndex),
        value,
        ...attribute.instanceValues.slice(instanceIndex + 1),
      ];
      return { ...attribute, instanceValues };
    }
    return attribute;
  });
};

const onDeleteInstance = (instanceId: string) => {
  if (form.instances.length <= 1 && !form.dynamicInstances) {
    return;
  }

  const instanceIndex = form.instances.findIndex((instance) => instance.id === instanceId);

  /** update attributes' instanceValues */
  form.attributes = form.attributes.map((attribute) => {
    const instanceValues = [
      ...attribute.instanceValues.slice(0, instanceIndex),
      ...attribute.instanceValues.slice(instanceIndex + 1),
    ];
    return { ...attribute, instanceValues };
  });

  form.instances = form.instances
    .filter((instance) => instance.id !== instanceId)
    .map((instance, index) => ({ ...instance, name: buildInstanceName(index + 1) }));
};

/**
 * Define the location to return after save/deletion/cancel
 */
const backLink = { name: links.games.classes.list, params: { gameId: props.gameId, versionId: props.versionId } };

const editLink = {
  name: links.games.classes.edit,
  params: { gameId: props.gameId, versionId: props.versionId, classId: props.classId },
};

/**
 * Save the Class
 */
const { mutate: saveClass, loading: saveLoading } = useClassFormPageSaveMutation({
  refetchQueries: [queryNames.ClassesListPage],
});

const saveForm = async () => {
  const buildInstance = (instance: FormInstance, index: number) => ({
    id: instance.id.startsWith("__") ? undefined : instance.id /* remove temporary ID */,
    enabled: true,
    values: form.attributes.map((attribute) => ({
      technicalName: attribute.technicalName,
      value: attribute.instanceValues[index],
    })),
  });

  const input: ClassFormInput = {
    technicalName: form.technicalName,
    displayName: form.displayName,
    description: form.description,
    isMultiInstance: form.isMultiInstance,
    dynamicInstances: form.dynamicInstances,
    attributes: form.attributes.map((attr) => ({
      displayName: attr.displayName,
      technicalName: attr.technicalName,
      type: Object.keys(inputTypeToAttributeTypeMapping).includes(attr.type)
        ? inputTypeToAttributeTypeMapping[attr.type]
        : attr.type,
      availableValues: Object.keys(inputTypeToAttributeTypeMapping).includes(attr.type) ? attr.availableValues : [],
    })),
    instances: (form.isMultiInstance ? form.instances : form.instances.slice(0, 1)).map(buildInstance),
  };

  const response = await saveClass({
    gameId: props.gameId,
    versionId: props.versionId,
    classId: props.classId,
    input,
  });

  const saveResult = response?.data?.saveClass;

  if (saveResult?.success) {
    toastSuccess({ detail: "Class saved successfully", summary: "Success", life: 3000 });

    router.push({
      ...backLink,
      hash: `#${props.classId ?? form.technicalName}`,
    });
  } else {
    if (saveResult?.isConflict) {
      formErrors.technicalName = "This technical name is already taken";
    }

    toastError({
      detail: saveResult?.errorMessage || "An error occurred while saving the class",
      summary: "Error",
    });
  }
};

/**
 * If the class is used in an A/B Test cohort or a configuration rule,
 * we cannot delete it without corrupting the data integrity.
 * We therefore display an information dialog that explain to the user
 * that he has to remove the class from those resource before continuing.
 */
const deleteWarningDetails = reactive<{
  abtests: NonNullable<DeleteClassMutationResponse["usedInAbtests"]>;
  configurationRules: NonNullable<DeleteClassMutationResponse["usedInConfigurationRules"]>;
}>({
  abtests: [],
  configurationRules: [],
});

const displayDeleteWarning = ref(false);

const closeDeleteWarning = () => {
  displayDeleteWarning.value = false;
};

/**
 * Delete the Class
 */
const { mutate: deleteClass } = useClassFormPageDeleteMutation({
  refetchQueries: [queryNames.ClassesListPage],
});

const onDelete = async () => {
  if (!props.classId) {
    return;
  }

  const response = await deleteClass({
    gameId: props.gameId,
    versionId: props.versionId,
    classId: props.classId,
  });

  const deleteResult = useMutationResponse(response, (resp) => resp?.deleteClass);
  if (deleteResult?.success) {
    router.push(backLink);
  } else if (deleteResult?.isCurrentlyUsed) {
    deleteWarningDetails.abtests = deleteResult?.usedInAbtests ?? [];
    deleteWarningDetails.configurationRules = deleteResult?.usedInConfigurationRules ?? [];
    displayDeleteWarning.value = true;
  }
};

const getColumnLabel = (index: number) => {
  if (form.dynamicInstances) {
    return `Instance ${index}`;
  }
  if (form.instances.length === 1) {
    return "Default Values";
  }
  if (form.instances.length > 1 && index === 1) {
    return `Instance ${index} (Default values)`;
  }
  return `Instance ${index}`;
};

watch(
  () => form.dynamicInstances,
  (dynamicInstances) => {
    if (dynamicInstances) {
      form.isMultiInstance = true;
    } else if (form.instances.length === 1) {
      form.isMultiInstance = false;
    }
  }
);

watch(
  () => form.instances,
  (instances) => {
    if (instances.length > 1) {
      form.isMultiInstance = true;
    } else if (!form.dynamicInstances) {
      form.isMultiInstance = false;
    }
  }
);
</script>

<style lang="scss">
.class-form-page {
  .vd-form__content {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 40px;
    align-items: center;

    .add-instance-button {
      display: inline-block;
      justify-self: end;
    }

    .multi-instances-attributes {
      display: flex;
    }

    .description {
      grid-column: span 2;
    }

    .attribute-name-cell {
      display: flex;
      align-items: center;

      .delete-button {
        margin-right: 10px;
        color: var(--vd-color-grey-light-1);
      }
    }

    .attributes-table {
      @extend %card;

      th > .cell {
        display: flex;
        justify-content: space-between;
      }
    }

    .el-table {
      grid-column: span 2;

      .display-name-form-field {
        width: 100%;
      }

      .el-form-item__error {
        position: initial;
      }

      .vd-select {
        width: 100% !important;
      }
    }

    .select-values-input {
      margin-top: 10px;
    }

    .attributes-controls {
      margin: 1.5rem 0;
    }

    .drawer-wrapper {
      grid-column: span 2;
      margin-bottom: 1.5rem;
      cursor: pointer;

      svg {
        margin-right: 0.5rem;
        font-size: 12px;
      }

      span {
        margin-right: 0.5rem;
      }
    }
  }
}
</style>
